import { useMemo, useState } from "react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Alert from "../common/Alert";
import {
  useLazyGetPosOrderInvoicesQuery,
  usePrintPosInvoiceReceiptMutation,
} from "../../store/api";
import { isEmptyPrintJobId } from "../../utils/posPrintHandler";

const BILL_NOT_READY_MESSAGE = "Bill is not ready yet. Please try again.";
const BILL_PRINT_SUCCESS_MESSAGE = "Bill sent to printer.";
const BILL_PRINT_FAILURE_MESSAGE =
  "Unable to print bill. Please check printer/device configuration.";

const getInvoiceId = (invoice) => invoice?.invoiceId || invoice?.id || "";

const getInvoiceAmount = (invoice) =>
  Number(
    invoice?.grandTotal ??
      invoice?.total ??
      invoice?.totalAmount ??
      invoice?.amount ??
      invoice?.balanceDue ??
      invoice?.totals?.grandTotal ??
      0,
  );

const normalizeInvoices = (response) => {
  const source = Array.isArray(response)
    ? response
    : Array.isArray(response?.invoices)
      ? response.invoices
      : Array.isArray(response?.items)
        ? response.items
        : [];

  return source
    .filter((invoice) => invoice && getInvoiceId(invoice))
    .map((invoice) => ({
      ...invoice,
      invoiceId: getInvoiceId(invoice),
    }));
};

const formatInvoiceAmount = (invoice) => {
  const amount = getInvoiceAmount(invoice);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `$${amount.toFixed(2)}`;
};

const formatInvoiceDate = (invoice) => {
  const value = invoice?.createdAt || invoice?.invoiceDate || invoice?.issuedAt;
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const PrintBillAction = ({
  orderId,
  className = "",
  size = "sm",
  variant = "secondary",
  disabled = false,
  onSuccess,
  onError,
}) => {
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [lookupInvoices, { isFetching: isLookingUpInvoices }] =
    useLazyGetPosOrderInvoicesQuery();
  const [printInvoice, { isLoading: isPrinting }] =
    usePrintPosInvoiceReceiptMutation();

  const isProcessing = isLookingUpInvoices || isPrinting;
  const selectedInvoice = useMemo(
    () =>
      invoiceOptions.find(
        (invoice) => String(invoice.invoiceId) === String(selectedInvoiceId),
      ) || null,
    [invoiceOptions, selectedInvoiceId],
  );

  const notifyError = () => {
    onError?.(BILL_PRINT_FAILURE_MESSAGE);
  };

  const printSelectedInvoice = async (invoiceId, { closeModal = false } = {}) => {
    if (!invoiceId || isPrinting) return;

    try {
      const result = await printInvoice(invoiceId).unwrap();
      if (isEmptyPrintJobId(result?.printJobId)) {
        notifyError();
        return;
      }
      if (closeModal) {
        setInvoiceModalOpen(false);
        setSelectedInvoiceId("");
        setInvoiceOptions([]);
      }
      onSuccess?.(BILL_PRINT_SUCCESS_MESSAGE);
    } catch {
      notifyError();
    }
  };

  const handlePrintBill = async () => {
    if (!orderId || disabled || isProcessing) return;

    try {
      const response = await lookupInvoices(orderId, true).unwrap();
      const validInvoices = normalizeInvoices(response);

      if (validInvoices.length === 0) {
        onError?.(BILL_NOT_READY_MESSAGE);
        return;
      }

      if (validInvoices.length === 1) {
        await printSelectedInvoice(validInvoices[0].invoiceId);
        return;
      }

      setInvoiceOptions(validInvoices);
      setSelectedInvoiceId("");
      setInvoiceModalOpen(true);
    } catch {
      onError?.(BILL_NOT_READY_MESSAGE);
    }
  };

  const handleConfirmInvoiceSelection = () => {
    if (!selectedInvoiceId || isProcessing) return;
    void printSelectedInvoice(selectedInvoiceId, { closeModal: true });
  };

  const handleCloseModal = () => {
    if (isProcessing) return;
    setInvoiceModalOpen(false);
    setSelectedInvoiceId("");
    setInvoiceOptions([]);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={handlePrintBill}
        disabled={disabled || isProcessing || !orderId}
      >
        {isProcessing ? "Printing..." : "Print Bill"}
      </Button>

      <Modal
        isOpen={invoiceModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmInvoiceSelection}
        title="Select Invoice"
        confirmText="Print Bill"
        confirmDisabled={!selectedInvoiceId}
        isLoading={isPrinting}
        size="lg"
      >
        <div className="space-y-4">
          <Alert tone="info">
            This order has multiple invoices. Select the invoice to print.
          </Alert>
          <div className="space-y-2">
            {invoiceOptions.map((invoice) => {
              const invoiceId = invoice.invoiceId;
              const invoiceLabel =
                invoice.invoiceNo ||
                invoice.invoiceNumber ||
                invoice.splitName ||
                invoiceId;
              const amount = formatInvoiceAmount(invoice);
              const date = formatInvoiceDate(invoice);

              return (
                <label
                  key={invoiceId}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm transition hover:border-indigo-300 dark:border-slate-700"
                >
                  <input
                    type="radio"
                    name="printBillInvoice"
                    value={invoiceId}
                    checked={String(selectedInvoiceId) === String(invoiceId)}
                    onChange={() => setSelectedInvoiceId(invoiceId)}
                    className="mt-1 h-4 w-4 text-indigo-600"
                    disabled={isProcessing}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-900 dark:text-slate-100">
                      {invoiceLabel}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                      {[
                        amount,
                        invoice.status,
                        date,
                        `ID: ${invoiceId}`,
                      ]
                        .filter(Boolean)
                        .join(" | ")}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {selectedInvoice ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Selected invoice: {selectedInvoice.invoiceNo || selectedInvoice.invoiceId}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default PrintBillAction;
